import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  // Collection service imports
  getCollections,
  getCollectionById,
  searchCollections,
  createCollection,
  updateCollection,
  deleteCollection,
  addProductsToCollection,
  removeProductsFromCollection,
  FetchCollectionsOptions,
  SearchCollectionsOptions,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  AddProductsToCollectionRequest,
  RemoveProductsFromCollectionRequest
} from '@/services/collectionService';
import {
  // Redux action imports
  fetchCollectionsRequest,
  fetchCollectionsSuccess,
  fetchCollectionsFailure,
  fetchCollectionRequest,
  fetchCollectionSuccess,
  fetchCollectionFailure,
  createCollectionRequest,
  createCollectionSuccess,
  createCollectionFailure,
  updateCollectionRequest,
  updateCollectionSuccess,
  updateCollectionFailure,
  deleteCollectionRequest,
  deleteCollectionSuccess,
  deleteCollectionFailure,
  searchCollectionsRequest,
  searchCollectionsSuccess,
  searchCollectionsFailure,
  addProductsToCollectionRequest,
  addProductsToCollectionSuccess,
  addProductsToCollectionFailure,
  removeProductsFromCollectionRequest,
  removeProductsFromCollectionSuccess,
  removeProductsFromCollectionFailure,
} from '@/store/slices/collection.slice';
import { ApiError } from "@/lib/apiClient";

// Fetch Collections Saga
function* fetchCollectionsSaga(action: PayloadAction<FetchCollectionsOptions>): Generator<any, void, any> {
  try {
    const response: any = yield call(getCollections, action.payload);
    
    yield put(fetchCollectionsSuccess({
      list: response.list,
      page: response.page,
      total: response.total,
      pageSize: response.pageSize,
      totalPages: response.totalPages,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchCollectionsFailure(error));
    } else {
      yield put(
        fetchCollectionsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Single Collection Saga
function* fetchCollectionSaga(action: PayloadAction<{ collectionId: string }>): Generator<any, void, any> {
  try {
    const { collectionId } = action.payload;
    const response: any = yield call(getCollectionById, collectionId);

    yield put(fetchCollectionSuccess({ collection: response }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchCollectionFailure(error));
    } else {
      yield put(
        fetchCollectionFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Search Collections Saga
function* searchCollectionsSaga(action: PayloadAction<SearchCollectionsOptions>): Generator<any, void, any> {
  try {
    const response: any = yield call(searchCollections, action.payload);
    
    yield put(searchCollectionsSuccess({
      list: response.list,
      page: response.page,
      total: response.total,
      pageSize: response.pageSize,
      totalPages: response.totalPages,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(searchCollectionsFailure(error));
    } else {
      yield put(
        searchCollectionsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Create Collection Saga
function* createCollectionSaga(action: PayloadAction<CreateCollectionRequest>): Generator<any, void, any> {
  try {
    const response: any = yield call(createCollection, action.payload);
    
    yield put(createCollectionSuccess({ collection: response }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(createCollectionFailure(error));
    } else {
      yield put(
        createCollectionFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Update Collection Saga
function* updateCollectionSaga(action: PayloadAction<{ collectionId: string; collection: UpdateCollectionRequest }>): Generator<any, void, any> {
  try {
    const { collectionId, collection } = action.payload;
    const response: any = yield call(updateCollection, collectionId, collection);

    yield put(updateCollectionSuccess({ collection: response }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(updateCollectionFailure(error));
    } else {
      yield put(
        updateCollectionFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Delete Collection Saga
function* deleteCollectionSaga(action: PayloadAction<{ collectionId: string }>): Generator<any, void, any> {
  try {
    const { collectionId } = action.payload;
    yield call(deleteCollection, collectionId);

    yield put(deleteCollectionSuccess({ collectionId }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(deleteCollectionFailure(error));
    } else {
      yield put(
        deleteCollectionFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Add Products to Collection Saga
function* addProductsToCollectionSaga(action: PayloadAction<{ collectionId: string; productIds: string[] }>): Generator<any, void, any> {
  try {
    const { collectionId, productIds } = action.payload;
    const request: AddProductsToCollectionRequest = { product_ids: productIds };
    const response: any = yield call(addProductsToCollection, collectionId, request);

    yield put(addProductsToCollectionSuccess({
      collectionId,
      addedCount: response.added || productIds.length
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(addProductsToCollectionFailure(error));
    } else {
      yield put(
        addProductsToCollectionFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Remove Products from Collection Saga
function* removeProductsFromCollectionSaga(action: PayloadAction<{ collectionId: string; productIds: string[] }>): Generator<any, void, any> {
  try {
    const { collectionId, productIds } = action.payload;
    const request: RemoveProductsFromCollectionRequest = { product_ids: productIds };
    const response: any = yield call(removeProductsFromCollection, collectionId, request);

    yield put(removeProductsFromCollectionSuccess({
      collectionId,
      removedCount: response.removed || productIds.length
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(removeProductsFromCollectionFailure(error));
    } else {
      yield put(
        removeProductsFromCollectionFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Root Collection Saga
export default function* collectionsRootSaga(): Generator<any, void, any> {
  yield takeLatest(fetchCollectionsRequest.type, fetchCollectionsSaga);
  yield takeLatest(fetchCollectionRequest.type, fetchCollectionSaga);
  yield takeLatest(searchCollectionsRequest.type, searchCollectionsSaga);
  yield takeEvery(createCollectionRequest.type, createCollectionSaga);
  yield takeEvery(updateCollectionRequest.type, updateCollectionSaga);
  yield takeEvery(deleteCollectionRequest.type, deleteCollectionSaga);
  yield takeEvery(addProductsToCollectionRequest.type, addProductsToCollectionSaga);
  yield takeEvery(removeProductsFromCollectionRequest.type, removeProductsFromCollectionSaga);
}